/**
 * Deterministic randomness for the simulated demo data.
 *
 * Every generator seeds from a fixed string (never `Date.now()`, never
 * `Math.random()`), so a reload shows exactly the same numbers. That matters
 * twice over: the developer reading the demo can quote a figure and find it
 * again, and a screenshot taken today still matches the app tomorrow.
 */

/** FNV-1a — small, fast, stable across runs. */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** mulberry32, seeded from a string. Returns floats in [0, 1). */
export function makeRng(seed: string): () => number {
  let a = hashString(seed) || 1;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A single stable float in [min, max) for a seed — no generator to carry around. */
export function seededFloat(seed: string, min: number, max: number): number {
  return min + (hashString(seed) / 4294967296) * (max - min);
}

/** A single stable integer in [min, max] for a seed. */
export function seededInt(seed: string, min: number, max: number): number {
  return Math.floor(seededFloat(seed, min, max + 1 - Number.EPSILON));
}

/** Stable pick from a list. Empty list → undefined. */
export function seededPick<T>(seed: string, list: readonly T[]): T | undefined {
  if (!list.length) return undefined;
  return list[seededInt(seed, 0, list.length - 1)];
}

/**
 * A stable multiplier around 1 (e.g. `jitter(k, 0.15)` → 0.85‥1.15). Used to
 * keep derived cost lines from looking rubber-stamped off a single ratio.
 */
export function jitter(seed: string, spread: number): number {
  return 1 + (seededFloat(seed, 0, 2) - 1) * spread;
}

/** Memoize an async factory so a table is derived once per page load. */
export function once<T>(fn: () => Promise<T>): () => Promise<T> {
  let p: Promise<T> | null = null;
  return () => {
    if (!p) {
      p = fn().catch((err) => {
        p = null;
        throw err;
      });
    }
    return p;
  };
}
